import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Resolve a short-lived signed URL for a private stored file (owner/admin only).
export const signedFileUrl = async (path) => {
  const r = await api.get("/files/sign", { params: { path } });
  return `${API}/files/${path}?token=${r.data.token}`;
};

export const SESSION_EXPIRED = "ccf:session-expired";

const NO_REFRESH = ["/auth/login", "/auth/refresh", "/auth/register", "/auth/logout", "/auth/google"];
const skipRefresh = (url = "") => NO_REFRESH.some((p) => url.includes(p));

const clearSession = () => {
  localStorage.removeItem("ccf_token");
  localStorage.removeItem("ccf_refresh");
};

const expiresSoon = (token) => {
  try {
    const p = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return p.exp * 1000 - Date.now() < 30000;
  } catch {
    return true;
  }
};

// Single-flight refresh shared by every concurrent request.
let refreshing = null;
const refreshAccess = () => {
  if (!refreshing) {
    refreshing = axios.post(`${API}/auth/refresh`, { refresh_token: localStorage.getItem("ccf_refresh") })
      .then((res) => { localStorage.setItem("ccf_token", res.data.access_token); return res.data.access_token; })
      .catch((e) => {
        clearSession();
        window.dispatchEvent(new Event(SESSION_EXPIRED));
        throw e;
      })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
};

// Proactive refresh: an expired access token is renewed BEFORE the request, so panels never fire 401s.
api.interceptors.request.use(async (config) => {
  let token = localStorage.getItem("ccf_token");
  if (token && expiresSoon(token) && localStorage.getItem("ccf_refresh") && !skipRefresh(config.url)) {
    try {
      token = await refreshAccess();
    } catch {
      token = null;
      if (config.url?.includes("/auth/me")) throw new axios.Cancel("session expired");
    }
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Fallback: token revoked server-side -> refresh once and retry.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && localStorage.getItem("ccf_refresh") && original && !original._retry && !skipRefresh(original.url)) {
      original._retry = true;
      try {
        const token = await refreshAccess();
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch { /* session cleared in refreshAccess */ }
    }
    return Promise.reject(error);
  }
);
